import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export interface ProductAndPageNotFoundStyles {
    pageTitle?: TypographyPresentationProps;
    searchText?: InjectableCss;
    searchTerm?: InjectableCss;
    pageNotFoundWrapper?: TypographyPresentationProps;
    pageBlock?: InjectableCss;
    helpMsg?: TypographyPresentationProps;
    buttonWrapper?: InjectableCss;
    homepageBtn?: InjectableCss;
    separator?: InjectableCss;
    noSearchResultMsg?: InjectableCss;
}

export const productAndPageNotFoundStyles: ProductAndPageNotFoundStyles = {
    pageNotFoundWrapper: {
        css: css`
            min-height: 480px;
        `,
    },
    pageBlock: {
        css: css`
            border: 1px solid #d8d8d8;
            background-color: #fbfbfb;
            padding: 30px 20px;
            max-width: 600px;
        `,
    },
    pageTitle: {
        size: 20,
        css: css`
            text-align: center;
        `,
    },
    helpMsg: {
        size: 16,
        css: css`
            text-align: center;
        `,
    },
    buttonWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            & div {
                flex-basis: 48%;
                flex-wrap: wrap;
                padding: 10px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 5px;
                        `,
                        css`
                            padding: 5px;
                        `,
                        null,
                        null,
                        null,
                    ])}
            }
            & button {
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            padding: 0 10px;
                        `,
                        css`
                            padding: 0 10px;
                        `,
                        null,
                        null,
                        null,
                    ])}
                & span {
                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                                font-size: 12px;
                            `,
                            css`
                                font-size: 12px;
                            `,
                            css`
                                font-size: 12px;
                            `,
                            null,
                            null,
                        ])}
                }
            }
        `,
    },
    homepageBtn: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
    separator: {
        css: css`
            text-align: center;
            padding: 10px;
        `,
    },
    searchText: {
        css: css`
            font-size: 19px;
        `,
    },
    searchTerm: {
        css: css`
            font-weight: 700;
        `,
    },
    noSearchResultMsg: {
        css: css`
            padding-bottom: 20px;
        `,
    },
};

const StyledSpan = getStyledWrapper("span");

interface OwnProps {
    pageType?: string;
    isOrderHistoryProduct?:string;
    extendedStyles?: ProductAndPageNotFoundStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getProductListDataViewProperty(state, "pagination");
    const correctedQuery = getProductListDataViewProperty(state, "correctedQuery");
    const { productFilters } = state.pages.productList;
    const brandsPagePageLink = getPageLinkByPageType(state, "BrandsPage");
    const categoryListPageLink = getPageLinkByPageType(state, "CategoryListPage");
    const homePageLink = getPageLinkByPageType(state, "HomePage");

    return {
        totalItemCount: pagination?.totalItemCount,
        query: correctedQuery || productFilters.query,
        brandsPagePageUrl: brandsPagePageLink?.url,
        categoryListPageUrl: categoryListPageLink?.url,
        homePageUrl: homePageLink?.url,
    };
};

type Props = OwnProps & HasHistory & ReturnType<typeof mapStateToProps>;

const ProductAndPageNotFound: FC<Props> = ({
    query,
    brandsPagePageUrl,
    categoryListPageUrl,
    homePageUrl,
    history,
    pageType,
    extendedStyles,
    isOrderHistoryProduct,
}) => {
    const [styles] = useState(() => mergeToNew(productAndPageNotFoundStyles, extendedStyles));

    const handleRedirection = (url: string | undefined): void => {
        if (url) {
            history.push(url);
        }
    };

    return (
        <StyledWrapper data-test-selector="productListNoneFound" {...styles.pageNotFoundWrapper}>
            {pageType === "NoSearchResultPage" ? (
                <StyledWrapper {...styles.noSearchResultMsg}>
                    <Typography as="p" {...styles.searchText}>
                        {translate("Sorry, no products were found matching your search for: ")}
                        <StyledSpan {...styles.searchTerm}>{query}</StyledSpan>
                    </Typography>
                </StyledWrapper>
            ) : (
                ""
            )}
            <StyledWrapper {...styles.pageBlock}>
                {pageType === "NoSearchResultPage" ? (
                    <Typography variant="p" {...styles.pageTitle}>
                        {translate("We're sorry, your search returned no results.")}
                    </Typography>
                ) : (
                    <StyledWrapper>
                        <Typography variant="p" {...styles.pageTitle}>
                            {translate("Oops! We can’t seem to find the page you are looking for")}
                        </Typography>
                        <Typography variant="p" {...styles.helpMsg}>
                            {translate("Make sure you typed the correct URL or followed a valid link")}
                        </Typography>
                    </StyledWrapper>
                )}
                <Typography variant="p" {...styles.helpMsg}>
                    {translate("Maybe we can help with these...")}
                </Typography>
                {isOrderHistoryProduct && isOrderHistoryProduct==="1"?(
                    <StyledWrapper {...styles.homepageBtn}>
                    <Button variant="primary" onClick={() => handleRedirection("/MyAccount/RecentlyPurchasedItems")}>
                        {translate("Recently Purchased Items")}
                    </Button>
                </StyledWrapper>):(
                <StyledWrapper {...styles.buttonWrapper}>
                    <StyledWrapper>
                        <Button variant="primary" onClick={() => handleRedirection(brandsPagePageUrl)}>
                            {translate("Shop By Brand")}
                        </Button>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Button variant="primary" onClick={() => handleRedirection(categoryListPageUrl)}>
                            {translate("Shop By Category")}
                        </Button>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Button variant="primary" onClick={() => handleRedirection("/Divisions")}>
                            {translate("Divisions")}
                        </Button>
                    </StyledWrapper>
                    <StyledWrapper>
                        <Button variant="primary" onClick={() => handleRedirection("/ContactUs")}>
                            {translate("Contact Us")}
                        </Button>
                    </StyledWrapper>
                  
                </StyledWrapper>
                )}
                <StyledWrapper {...styles.separator}>{translate("Or")}</StyledWrapper>
                <StyledWrapper {...styles.homepageBtn}>
                    <Button variant="primary" onClick={() => handleRedirection(homePageUrl)}>
                        {translate("Back to homepage")}
                    </Button>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withHistory(ProductAndPageNotFound));
